/**
 * Pure presentation for the site map: tones, labels, accessible names, tooltips and the
 * side panel summary. Levels and reasons are described with the reliability view's labels
 * so the map and the site page never disagree.
 */

import { formatLastShotTracked, levelLabel, levelTone, reasonLabel } from '../../site';
import type { StatusTone } from '../../site';
import type {
  ClusterCounts,
  HealthSummary,
  SiteMapEntry,
  SiteMapHealth,
} from '../models/siteMapModels';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

function assertNever(value: never): never {
  throw new Error(`Unexpected value: ${String(value)}`);
}

export function entryTone(entry: SiteMapEntry): StatusTone {
  return entry.health ? levelTone(entry.health.level) : 'neutral';
}

export function entryHealthLabel(entry: SiteMapEntry): string {
  return entry.health ? levelLabel(entry.health.level) : 'No health data';
}

/** Reasons for Suspect, a reassurance for Unknown, nothing otherwise. */
export function entryHealthDetail(entry: SiteMapEntry): string | null {
  if (!entry.health) return null;
  const { level, reasons } = entry.health;
  switch (level) {
    case 'Ok':
      return null;
    case 'Suspect':
      return reasons.length > 0 ? reasons.map(reasonLabel).join(' · ') : 'Problem suspected';
    case 'Unknown':
      return 'Status not confirmed';
    default:
      return assertNever(level);
  }
}

export function isActive(entry: SiteMapEntry): boolean {
  return (entry.health?.recentShots ?? 0) > 0;
}

export function formatShotCount(count: number): string {
  return `${count} shot${count === 1 ? '' : 's'}`;
}

/** The health activity window, e.g. 300 → "5 min". */
export function formatActivityWindow(seconds: number): string {
  return seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`;
}

/** Relative time of the latest shot. Absent means the site has never delivered one. */
export function lastShotText(lastShotAt: string | undefined, now: Date): string {
  return lastShotAt === undefined ? 'No shots recorded' : formatLastShotTracked(lastShotAt, now).text;
}

/** When health was evaluated, e.g. "Updated 3 min ago". */
export function formatUpdated(evaluatedAt: string, now: Date): string {
  const time = new Date(evaluatedAt).getTime();
  if (Number.isNaN(time)) return 'Updated at an unknown time';
  const ageMs = now.getTime() - time;
  if (ageMs < MINUTE_MS) return 'Updated just now';
  if (ageMs < HOUR_MS) return `Updated ${Math.floor(ageMs / MINUTE_MS)} min ago`;
  return `Updated ${Math.floor(ageMs / HOUR_MS)} h ago`;
}

export interface Freshness {
  readonly tone: 'normal' | 'warning';
  readonly text: string;
}

/** How current the health shown on the map is. */
export function describeFreshness(
  health: {
    readonly data: SiteMapHealth | null;
    readonly isLoading: boolean;
    readonly error: string | null;
  },
  now: Date
): Freshness {
  if (health.data) {
    const updated = formatUpdated(health.data.evaluatedAt, now);
    return health.error
      ? { tone: 'warning', text: `Health could not be refreshed. ${updated}` }
      : { tone: 'normal', text: updated };
  }
  return health.error
    ? { tone: 'warning', text: 'Health could not be loaded. Sites are shown without health.' }
    : { tone: 'normal', text: 'Loading health…' };
}

/** Short name for a site marker, e.g. "Upsala GK, Suspect, active". */
export function siteAccessibleName(entry: SiteMapEntry): string {
  return [entry.site.name, entryHealthLabel(entry), ...(isActive(entry) ? ['active'] : [])].join(', ');
}

export interface DescribeContext {
  readonly now: Date;
  readonly recentShotsWindowS: number;
}

/** Multi-line hover details for a site marker. */
export function siteTooltip(entry: SiteMapEntry, context: DescribeContext): string {
  const heading = `${entry.site.name} (${entry.site.siteId})`;
  if (!entry.health) return [heading, entryHealthLabel(entry)].join('\n');

  const detail = entryHealthDetail(entry);
  return [
    heading,
    detail ? `${entryHealthLabel(entry)} · ${detail}` : entryHealthLabel(entry),
    `Last shot: ${lastShotText(entry.health.lastShotAt, context.now)}`,
    `${formatShotCount(entry.health.recentShots)} in the last ${formatActivityWindow(context.recentShotsWindowS)}`,
  ].join('\n');
}

/** Name for a cluster marker, e.g. "12 sites: 9 OK, 2 Suspect, 1 Unknown". */
export function clusterAccessibleName(counts: ClusterCounts): string {
  const total = counts.ok + counts.warning + counts.neutral;
  const parts: readonly (readonly [number, string])[] = [
    [counts.ok, 'OK'],
    [counts.warning, 'Suspect'],
    [counts.neutral, 'Unknown'],
    [counts.active, 'active'],
  ];
  const breakdown = parts
    .filter(([count]) => count > 0)
    .map(([count, label]) => `${count} ${label}`)
    .join(', ');
  return `${total} site${total === 1 ? '' : 's'}: ${breakdown}`;
}

export function summarise(entries: readonly SiteMapEntry[]): HealthSummary {
  const countWhere = (predicate: (entry: SiteMapEntry) => boolean): number =>
    entries.filter(predicate).length;
  return {
    total: entries.length,
    ok: countWhere((e) => e.health?.level === 'Ok'),
    suspect: countWhere((e) => e.health?.level === 'Suspect'),
    unknown: countWhere((e) => e.health?.level === 'Unknown'),
    noData: countWhere((e) => e.health === undefined),
    active: countWhere(isActive),
  };
}

/** Suspect sites, busiest first (most players affected), then by name. */
export function attentionList(entries: readonly SiteMapEntry[]): readonly SiteMapEntry[] {
  return entries
    .filter((e) => e.health?.level === 'Suspect')
    .slice()
    .sort(
      (a, b) =>
        (b.health?.recentShots ?? 0) - (a.health?.recentShots ?? 0) ||
        a.site.name.localeCompare(b.site.name)
    );
}
